import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  ChevronLeft, ChevronRight, MessageCircle, Brain, Users, Shield, 
  Clock, Award, BarChart3, Globe, Smartphone, CheckCircle, Star,
  GraduationCap, Target, TrendingUp, Heart, Zap, Lock, Phone, Mail, BookOpen
} from "lucide-react";

export default function SalesPresentation() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    // Slide 1: Title
    {
      id: "title",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            India's First WhatsApp-Based AI Tutor
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Your Child's
            <span className="text-emerald-600 block">Personal Tutor</span>
            <span className="text-amber-500">On WhatsApp</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mb-8">
            No app downloads. No boring videos. Just smart, conversational learning 
            that adapts to your child — in their language, at their pace.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8">
              Start 30-Day Free Trial
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">No credit card required • Cancel anytime</p>
        </div>
      )
    },

    // Slide 2: The Problem
    {
      id: "problem",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
            The Problem with Traditional EdTech
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-red-800">Another App to Download</h3>
                <p className="text-red-700">Kids have to learn a new interface. Parents worry about screen time.</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-red-800">One-Size-Fits-All</h3>
                <p className="text-red-700">Pre-recorded videos don't adapt to your child's learning pace or style.</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-red-800">No Real Engagement</h3>
                <p className="text-red-700">Kids get bored. Parents can't track real progress.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Slide 3: The Solution
    {
      id: "solution",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            Eduzy: Learning Where Kids Already Are
          </h2>
          <p className="text-xl text-gray-600 mb-8 text-center max-w-3xl">
            WhatsApp is already on every phone. Your child already knows how to use it. 
            We turn it into a powerful, personalized learning tool.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Conversational Learning</h3>
                <p className="text-gray-600">Natural chat-based lessons that feel like talking to a friendly tutor</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">AI-Powered Adaptation</h3>
                <p className="text-gray-600">GPT-powered lessons that adapt in real-time to your child's responses</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">11 Indian Languages</h3>
                <p className="text-gray-600">English, Hindi, Tamil, Telugu, Kannada, Marathi, Punjabi</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Curriculum Aligned</h3>
                <p className="text-gray-600">Covers CBSE/ICSE syllabus for Classes 5-8 in Math, Science, English & more</p>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 4: How It Works
    {
      id: "how-it-works",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
            How Eduzy Works
          </h2>
          <div className="flex flex-col md:flex-row gap-8 max-w-5xl items-center">
            {/* WhatsApp Chat Mockup */}
            <div className="w-full md:w-1/2">
              <div className="bg-gray-100 rounded-3xl p-4 shadow-xl max-w-sm mx-auto">
                <div className="bg-emerald-700 rounded-t-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Eduzy AI Tutor</p>
                    <p className="text-emerald-200 text-xs">Online</p>
                  </div>
                </div>
                <div className="bg-[#e5ddd5] p-4 space-y-3 min-h-[300px]">
                  <div className="bg-white rounded-lg p-3 max-w-[85%] shadow-sm">
                    <p className="text-sm">Hi Arjun! Ready to learn Math today?</p>
                    <p className="text-xs text-gray-400 text-right">9:30 AM</p>
                  </div>
                  <div className="bg-emerald-100 rounded-lg p-3 max-w-[85%] ml-auto shadow-sm">
                    <p className="text-sm">Yes! Let's start</p>
                    <p className="text-xs text-gray-400 text-right">9:31 AM</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 max-w-[85%] shadow-sm">
                    <p className="text-sm font-medium">Today's Topic: Fractions!</p>
                    <p className="text-sm mt-2">A fraction represents a part of a whole. If you have a pizza cut into 4 equal parts...</p>
                    <p className="text-xs text-gray-400 text-right">9:31 AM</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 max-w-[85%] shadow-sm">
                    <p className="text-sm font-medium">Quick Question:</p>
                    <p className="text-sm mt-1">If you have 3/4 of a pizza and eat 1/4, how much is left?</p>
                    <p className="text-sm mt-2">A) 1/2 &nbsp; B) 2/4 &nbsp; C) 1/4</p>
                    <p className="text-xs text-gray-400 text-right">9:32 AM</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Steps */}
            <div className="w-full md:w-1/2 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="font-bold text-lg">Register via WhatsApp</h3>
                  <p className="text-gray-600">Parent sends "JOIN EDUZY" to our number, verifies email, adds child details</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="font-bold text-lg">Child Starts Learning</h3>
                  <p className="text-gray-600">Just type "START SESSION" and choose a subject. AI generates personalized lessons.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="font-bold text-lg">Learn & Get Assessed</h3>
                  <p className="text-gray-600">Interactive Q&A, instant feedback, adaptive difficulty based on responses</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="font-bold text-lg">Parents Track Progress</h3>
                  <p className="text-gray-600">Detailed analytics, weekly reports, and full control via Parent Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 5: For Parents
    {
      id: "for-parents",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            Complete Control for Parents
          </h2>
          <p className="text-xl text-gray-600 mb-8 text-center max-w-3xl">
            Monitor your child's learning journey with our comprehensive Parent Dashboard
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
            <Card className="border-emerald-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <BarChart3 className="w-10 h-10 text-emerald-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Real-Time Progress</h3>
                <p className="text-gray-600">See accuracy, streaks, and subject-wise performance at a glance</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Clock className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Session History</h3>
                <p className="text-gray-600">Review every learning session, topics covered, and questions asked</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <TrendingUp className="w-10 h-10 text-purple-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Weekly Reports</h3>
                <p className="text-gray-600">Automated progress reports delivered to your email every week</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Shield className="w-10 h-10 text-amber-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Safe & Secure</h3>
                <p className="text-gray-600">DPDP compliant. Parental consent required. Data privacy guaranteed.</p>
              </CardContent>
            </Card>
            <Card className="border-rose-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Heart className="w-10 h-10 text-rose-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Subscription Control</h3>
                <p className="text-gray-600">Upgrade, downgrade, or cancel anytime. Full flexibility.</p>
              </CardContent>
            </Card>
            <Card className="border-cyan-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Award className="w-10 h-10 text-cyan-600 mb-4" />
                <h3 className="font-bold text-lg mb-2">Certificates</h3>
                <p className="text-gray-600">Download achievement certificates to celebrate milestones</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Slide 6: Features
    {
      id: "features",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
            Premium Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">AI-Powered Study Plans</h3>
                <p className="text-gray-600">Personalized learning paths based on strengths and weaknesses</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">Practice Tests</h3>
                <p className="text-gray-600">Timed assessments with detailed score analysis</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">Leaderboard Rankings</h3>
                <p className="text-gray-600">Healthy competition with grade-wise and school-wise rankings</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">Progress Certificates</h3>
                <p className="text-gray-600">Downloadable certificates for achievements and milestones</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-rose-50 to-rose-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">Ask Any Doubt</h3>
                <p className="text-gray-600">24/7 doubt clearing with AI — just type "ASK" followed by your question</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg">Multilingual Support</h3>
                <p className="text-gray-600">Learn in English, Hindi, Tamil, Telugu, Kannada, Marathi, or Punjabi</p>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 7: Pricing
    {
      id: "pricing",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            30-Day Free Trial on All Plans
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
            Simple, Transparent Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
            {/* Starter */}
            <Card className="border-2 border-gray-200 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 -mx-6 -mt-6 p-6 rounded-t-lg mb-6">
                  <h3 className="text-white font-bold text-xl">Starter</h3>
                  <p className="text-3xl font-bold text-white mt-2">₹499<span className="text-sm font-normal">/subject/month</span></p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /> 1 hour/day learning</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /> WhatsApp Learning</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /> Basic Progress Tracking</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /> Parent Dashboard (View)</li>
                </ul>
              </CardContent>
            </Card>

            {/* Plus - Recommended */}
            <Card className="border-2 border-blue-500 relative shadow-lg scale-105">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-4 py-1 rounded-full font-medium">
                MOST POPULAR
              </div>
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 -mx-6 -mt-6 p-6 rounded-t-lg mb-6">
                  <h3 className="text-white font-bold text-xl">Plus</h3>
                  <p className="text-3xl font-bold text-white mt-2">₹699<span className="text-sm font-normal">/subject/month</span></p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> Everything in Starter</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> 2 hours/day learning</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> Leaderboard Access</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> Practice Tests</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> Weekly Reports</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-blue-500" /> Progress Certificates</li>
                </ul>
              </CardContent>
            </Card>

            {/* Pro */}
            <Card className="border-2 border-gray-200 hover:border-purple-300 transition-colors">
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 -mx-6 -mt-6 p-6 rounded-t-lg mb-6">
                  <h3 className="text-white font-bold text-xl">Pro</h3>
                  <p className="text-3xl font-bold text-white mt-2">₹899<span className="text-sm font-normal">/subject/month</span></p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> Everything in Plus</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> Unlimited daily learning</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> AI Study Plans</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> Percentile Rankings</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> Quarterly Reports</li>
                  <li className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-purple-500" /> Priority Support</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <p className="text-sm text-gray-500 mt-6 text-center">* Prices are per subject. Add multiple subjects for comprehensive learning.</p>
        </div>
      )
    },

    // Slide 8: Why Eduzy
    {
      id: "why-eduzy",
      content: (
        <div className="flex flex-col items-center justify-center h-full px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
            Why Parents Choose Eduzy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Zero Setup Required</h3>
                <p className="text-gray-600">No app downloads, no login credentials for kids. Just WhatsApp.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Screen Time Control</h3>
                <p className="text-gray-600">Daily limits ensure learning without excessive screen time.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Truly Personalized</h3>
                <p className="text-gray-600">AI adapts to each child's learning style and pace in real-time.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Affordable</h3>
                <p className="text-gray-600">Quality AI tutoring at a fraction of the cost of private tutors.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Mother Tongue Learning</h3>
                <p className="text-gray-600">Kids learn better in their own language. We support 11 Indian languages.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">Parent-First Design</h3>
                <p className="text-gray-600">Full transparency. You control everything. DPDP compliant.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 9: CTA
    {
      id: "cta",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8 bg-gradient-to-br from-emerald-500 to-emerald-700 -mx-8 -my-8 py-16">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Transform Your Child's Learning?
          </h2>
          <p className="text-xl text-emerald-100 max-w-2xl mb-8">
            Join thousands of Indian families who are making education fun, 
            personalized, and effective with Eduzy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg" 
              className="bg-white text-emerald-700 hover:bg-gray-100 text-lg px-8"
              onClick={() => navigate("/login?register=true")}
            >
              Start 30-Day Free Trial
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 text-lg px-8"
              onClick={() => navigate("/eduwise")}
            >
              View Pricing
            </Button>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center gap-6 text-white">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              <span>+91 9654006470</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              <span>info@contentiumtech.com</span>
            </div>
          </div>
          <p className="text-emerald-200 text-sm mt-8">
            © 2025 Eduzy by Contentium Tech | Made with love in India
          </p>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => navigate("/")}
            data-testid="eduzy-header-logo"
          >
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-gray-900">Eduzy</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 hidden sm:block">
              Slide {currentSlide + 1} of {slides.length}
            </span>
            <Button variant="outline" size="sm" onClick={() => navigate("/")}>
              Exit Presentation
            </Button>
          </div>
        </div>
      </header>

      {/* Slide Content */}
      <main className="flex-1 pt-16 pb-20 overflow-hidden">
        <div className="h-full max-w-7xl mx-auto px-8 py-8">
          {slides[currentSlide].content}
        </div>
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t py-4">
        <div className="max-w-md mx-auto px-4 flex items-center justify-center gap-4">
          <Button
            variant="outline"
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          {/* Slide indicators */}
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index === currentSlide ? "bg-emerald-600" : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
}
